<svelte:options customElement="dst-notifications-wrapper" />

<script lang="ts">
	import { register as registerClosables } from '@/lib/closable.ts';
	import { pushToDataLayer, trackUserAction } from '@/lib/tracking.ts';
	import { removeNewNotificationsIndicator } from '@/lib/utils.ts';
	import notifications from '@/stores/notification_store.ts';
	import { loginState } from '@derstandard/login-state';
	import { dstBellBadgeColor, dstHeaderBell, dstHeaderBellBadge } from '@derstandard/styling-toolkit/src/svg/lib.js';
	import { createMutationObserver } from '@derstandard/utils';
	import { mdiBell } from '@mdi/js';
	import { afterUpdate } from 'svelte';

	export let hasuseractions;
	export let isapp = false;

	let root: HTMLElement;
	let notificationsFlyout: HTMLElement;

	$: {
		if ((root && $notifications?.hasNewNotifications) || (root && isapp)) {
			(root.getRootNode() as ShadowRoot).host.classList.add('notifications-active');
		} else if (root) {
			(root.getRootNode() as ShadowRoot).host.classList.remove('notifications-active');
		}
	}
	$: ariaExpanded = false;

	loginState.subscribe(async (state) => {
		if (!state.isLoggedIn || !state.payload.mId) {
			return;
		}
		// @ts-expect-error Uses Vite resolve.alias
		await import('__NOTIFICATIONS_NEXT_URL__');
		void (await import('../DstCounter/DstCounter.webcomponent.svelte')).default;
	});

	const { disconnect, observe } = createMutationObserver({
		onChanged: (mutation) => {
			const target = mutation.target as Element;

			if (target.getAttribute('id') !== 'app-notifications') {
				return;
			}

			const attrName = mutation.attributeName;
			const newValue = target.getAttribute(attrName);

			ariaExpanded = newValue === 'true';
		},
	});

	afterUpdate(() => registerClosables(root));

	/**
	 *
	 * @param node
	 */
	function mutation(node: HTMLElement) {
		observe(node);

		return {
			destroy() {
				disconnect();
			},
		};
	}

	/**
	 * calls getNotification on opening the flyout
	 * @returns
	 */
	function toggleIsOpen() {
		const isOpen = notificationsFlyout.dataset?.closableActive !== 'true';

		if (isOpen) {
			trackUserAction($notifications.hasNewNotifications ? 'header_shownotif_new' : 'header_shownotif');
			pushToDataLayer({ event: 'notifications.show' });
			removeNewNotificationsIndicator();
		}
	}

	/**
	 * Click handler for the notifications tab change
	 */
	function clickNotificationsTab() {
		trackUserAction('header_tabchange_profile');
		removeNewNotificationsIndicator();
	}
</script>

<div class="dst-notifications" bind:this={root} use:mutation>
	<button
		aria-controls="app-notifications"
		aria-expanded={ariaExpanded}
		title="Mitteilungen"
		data-closable-target="app-notifications"
		on:click={toggleIsOpen}
		type="button"
	>
		<span> Mitteilungen</span>

		{#if $notifications.hasNewNotifications}
			{#if isapp}
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
					{@html dstBellBadgeColor}
				</svg>
			{:else}
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{@html dstHeaderBellBadge}
				</svg>
			{/if}
		{:else if isapp}
			<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
				<path fill="currentColor" d={mdiBell} />
			</svg>
		{:else}
			<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
				{@html dstHeaderBell}
			</svg>
		{/if}
	</button>

	<dst-flyout
		id="app-notifications"
		data-closable="app-notifications"
		data-closable-active="false"
		bind:this={notificationsFlyout}
	>
		{#if hasuseractions}
			<div class="flyout--tabs" slot="tabs">
				<button
					data-closable-target="app-useractions"
					data-closable-close="true"
					on:click={clickNotificationsTab}
					type="button"
				>
					Profil
				</button>

				<button type="button" disabled>
					Mitteilungen

					<dst-counter />
				</button>
			</div>
		{/if}
		<dst-notifications />
	</dst-flyout>
</div>

<style lang="scss" global>
	@use './DstNotificationsWrapper.scss';
</style>
